import { useEffect, useState } from "react";
import api from "@/lib/api";
import BookButton from "@/components/BookButton";
import { useSEO, breadcrumbLd } from "@/lib/seo";

export default function LocalGuide() {
  const [attractions, setAttractions] = useState([]);
  useEffect(() => {
    api.get("/attractions").then((r) => setAttractions(r.data)).catch(() => {});
  }, []);

  useSEO({
    title: "Local Guide — Carrollton, MO & Carroll County Attractions",
    description:
      "Van Meter State Park, Swan Lake National Wildlife Refuge, the World's Largest Pecan in Brunswick, Baltimore Bend Vineyard — the hyper-local guide to Carroll County, Missouri.",
    path: "/local-guide",
    jsonLd: breadcrumbLd([{ name: "Home", path: "/" }, { name: "Local Guide", path: "/local-guide" }]),
  });

  return (
    <div data-testid="local-guide-page">
      <section className="pt-36 pb-12 bg-canvas">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-8">
            <p className="font-mono-label text-ember mb-4">A local guide · Carrollton, MO</p>
            <h1 className="font-display text-6xl md:text-7xl text-ink leading-[0.98]">
              The good bits<br /><span className="italic">of Carroll County.</span>
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-ink-soft leading-relaxed">
              Our front desk is a curated tip-line for the region. This is the shortlist we hand to guests
              when they ask, &ldquo;What&apos;s worth doing while we&apos;re here?&rdquo; — verified, hyper-local, no fluff.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-canvas pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-px bg-rule-strong">
          {attractions.map((a, i) => (
            <article
              key={a.id}
              className={`bg-canvas overflow-hidden fade-up ${i % 5 === 0 ? "md:col-span-8" : i % 5 === 1 ? "md:col-span-4" : i % 5 === 2 ? "md:col-span-4" : i % 5 === 3 ? "md:col-span-8" : "md:col-span-6"}`}
              style={{ animationDelay: `${i * 60}ms` }}
              data-testid={`attraction-${i}`}
            >
              {a.image_url ? (
                <div className="aspect-[16/10] overflow-hidden bg-dune">
                  <img src={a.image_url} alt={a.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms]" loading="lazy" />
                </div>
              ) : (
                <div
                  className="aspect-[16/10] relative overflow-hidden flex items-center justify-center px-10"
                  style={{
                    background: "linear-gradient(135deg, #f5efe6 0%, #ede5d6 60%, #e6dcc8 100%)",
                  }}
                  data-testid={`attraction-${i}-placeholder`}
                >
                  <div
                    className="absolute inset-0 opacity-30"
                    style={{
                      backgroundImage:
                        "repeating-linear-gradient(45deg, transparent 0 24px, rgba(196,138,80,0.18) 24px 25px)",
                    }}
                  />
                  <div className="relative text-center">
                    <p className="font-mono-label text-ember mb-3 tracking-[0.25em]">{a.category}</p>
                    <p className="font-display text-3xl md:text-4xl text-ink italic leading-tight">{a.name}</p>
                    <p className="font-mono-label text-ink-muted mt-3 text-[11px]">Photography pending verification</p>
                  </div>
                </div>
              )}
              <div className="p-8 md:p-10">
                <p className="font-mono-label text-ember mb-3">{a.category} · {a.distance}</p>
                <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight">{a.name}</h2>
                <p className="mt-4 text-ink-soft leading-relaxed">{a.description}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-sand py-24" data-testid="zero-click-faq">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="font-mono-label text-ember mb-4">Quick answers</p>
            <h2 className="font-display text-5xl text-ink leading-tight">
              The stuff guests<br /><span className="italic">Google at 11pm.</span>
            </h2>
          </div>
          <div className="md:col-span-7 space-y-10">
            {[
              ["Where exactly is the inn?", "We're at 1408 N US-65, right on Highway 65 in Carrollton, Missouri. Look for our gradient W sign on the east side of the road — you'll see it from the highway."],
              ["What's the closest food after dark?", "Pizza Hut is next door — about a one-minute walk. A 24-hour fuel station with hot food is right past that. For a real sit-down, downtown Carrollton is five minutes by car."],
              ["Is there anything quiet to do nearby?", "Van Meter State Park (35 min) for hiking and the freshwater marsh boardwalk; Swan Lake National Wildlife Refuge (40 min) for migratory birds; Baltimore Bend Vineyard (30 min) for a slow afternoon."],
              ["Can I bring my truck or RV?", "Yes — we have plenty of easy in/out truck and bus parking. Mention 'trucker' when you call and we'll tell you about the discount."],
              ["Is the area kid-friendly?", "Absolutely. The World's Largest Pecan in Brunswick is a photo-op favourite. Van Meter has trails short enough for younger kids and a fishing lake."],
            ].map(([q, a]) => (
              <div key={q} className="border-t border-rule-strong pt-6">
                <h3 className="font-display text-2xl text-ink mb-2">{q}</h3>
                <p className="text-ink-soft leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-canvas py-20 text-center" data-testid="local-cta">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="font-mono-label text-ember mb-4">Plan around it</p>
          <h2 className="font-display text-5xl md:text-6xl text-ink leading-[1.02] max-w-3xl mx-auto">
            Make Westbridge<br /><span className="italic">your basecamp.</span>
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <BookButton label="Reserve a Room" testid="local-cta-book" />
          </div>
        </div>
      </section>
    </div>
  );
}
